import React from "react";
import { Link } from "react-router-dom";
import { Activity, Mic, Dna, ShieldCheck, ArrowRight, Stethoscope, FlaskConical, Users } from "lucide-react";

const STEPS = [
  { icon: Mic, title: "Voice Capture", desc: "Language-agnostic mic recording with an automated technical quality check." },
  { icon: Activity, title: "AI Triage", desc: "Validated model returns Mild / Moderate / High risk across 8 conditions." },
  { icon: Dna, title: "Genetic Pathway", desc: "Moderate & high results map to condition-specific genetic tests." },
  { icon: ShieldCheck, title: "Clinical Review", desc: "Voice + genetic layers unified under one MANOGENE ID for interpretation." },
];

const CONDITIONS = ["Depression", "Anxiety", "Parkinson's", "Stroke", "Cognitive Ability", "Dementia", "Early-Onset Dementia", "Alzheimer's"];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="inline-block">
            <img src="/manogene-logo.png" alt="ManoGene" className="h-9 w-auto" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="landing-login-link" className="text-sm font-semibold text-ink-soft hover:text-brand px-3 py-2">
              Sign in
            </Link>
            <Link to="/register" data-testid="landing-register-link"
              className="text-sm font-semibold bg-brand text-white rounded-lg px-4 py-2 hover:bg-brand-hover transition-colors">
              Get started
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 pt-16 pb-12 grid lg:grid-cols-2 gap-12 items-center">
        <div className="animate-fade-up">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-brand mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-accent" /> Clinical pilot · Voice-to-Genetics
          </div>
          <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-ink leading-[1.05]">
            Your voice, the first<br />
            <span className="text-brand">signal of your health.</span>
          </h1>
          <p className="mt-6 text-lg text-ink-soft leading-relaxed max-w-lg">
            ManoGene turns a short voice recording into a validated risk triage across eight neurological,
            cognitive and mental-health conditions — then bridges to targeted genetic testing under a single MANOGENE ID.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" data-testid="hero-cta"
              className="inline-flex items-center gap-2 bg-brand text-white rounded-xl px-6 py-3.5 font-semibold hover:bg-brand-hover transition-colors">
              Start screening <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/login"
              className="inline-flex items-center gap-2 border border-slate-300 rounded-xl px-6 py-3.5 font-semibold text-ink hover:bg-white transition-colors">
              I have an account
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap gap-2">
            {CONDITIONS.map((c) => (
              <span key={c} className="text-xs font-medium rounded-full bg-white border border-slate-200 px-3 py-1 text-ink-soft">
                {c}
              </span>
            ))}
          </div>
        </div>

        <div className="relative animate-fade-up" style={{ animationDelay: "0.1s" }}>
          <img
            src="https://images.unsplash.com/photo-1665396697251-05a3a49515a7"
            alt="Patient recording voice for screening"
            className="rounded-2xl shadow-2xl w-full object-cover aspect-[4/5] max-h-[520px]"
          />
          <div className="absolute -bottom-5 -left-5 bg-white rounded-xl shadow-lg border border-slate-100 p-4 hidden sm:block">
            <div className="eyebrow">Sample triage</div>
            <div className="mt-2 flex items-center gap-2">
              <span className="rounded-full bg-[#FEF2F2] text-[#B91C1C] border border-[#FCA5A5] px-2.5 py-1 text-xs font-semibold">High · Alzheimer's</span>
            </div>
            <div className="mono text-xs text-ink-muted mt-2">MG-ZUSSN5 · confidence 0.91</div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 py-16">
        <div className="grid md:grid-cols-4 gap-5">
          {STEPS.map((s, i) => (
            <div key={s.title} className="bg-white rounded-xl border border-slate-200 p-6 card-lift">
              <div className="h-11 w-11 rounded-xl bg-blue-50 flex items-center justify-center text-brand mb-4">
                <s.icon className="h-5 w-5" strokeWidth={2.2} />
              </div>
              <div className="eyebrow mb-1">Step {i + 1}</div>
              <h3 className="font-head font-semibold text-ink text-lg">{s.title}</h3>
              <p className="text-sm text-ink-soft mt-1.5 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 pb-20">
        <div className="rounded-2xl bg-brand text-white p-8 sm:p-12 grid sm:grid-cols-3 gap-8">
          {[
            { icon: Stethoscope, t: "For Clinicians", d: "Two-layer profile — voice triage beside genetic findings. No auto-diagnosis." },
            { icon: FlaskConical, t: "For Labs", d: "Dedicated queue with an 11-stage status machine and secure report upload." },
            { icon: Users, t: "For Patients", d: "Track each condition privately with consent you control at every step." },
          ].map((x) => (
            <div key={x.t}>
              <x.icon className="h-7 w-7 mb-3 text-blue-200" />
              <h3 className="font-head font-semibold text-xl">{x.t}</h3>
              <p className="text-sm text-blue-100 mt-2 leading-relaxed">{x.d}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-200 bg-white py-6">
        <div className="max-w-6xl mx-auto px-5 text-sm text-ink-muted flex flex-wrap justify-between gap-2">
          <span>© 2026 ManoGene · Voice-to-Genetics Screening Platform</span>
          <span>Pilot-grade security · Audit-logged PHI access</span>
        </div>
      </footer>
    </div>
  );
}
